<template>
  <div id="app">
    <ContributeLink />
    <FavIcon :pipelines="pipelines" />
    <FunSnow v-if="config.alpha_show_snow && show_snow_fun" />
    <background />

    <b-button
      class="options"
      @click="openConfiguration"
      v-if="!configuring"
      placement="left"
      title="Open options"
      aria-label="options"
    >
      open&nbsp;options
    </b-button>

    <configuration-form
      v-if="configuring"
      class="configuration-form"
      @onSubmit="onSubmit"
      :config="config"
    />

    <div class="fluid" v-else>
      <div v-if="error" class="error-message">
        <h3>{{ error }}</h3>
        <p>The dashboard will automatically retry every 60 seconds.</p>
        <b-button size="lg" variant="outline-danger" @click="retry"
          >Retry now
        </b-button>
      </div>

      <div class="fluid" v-else>
        <div v-if="loading">
          <loading-spinner />
        </div>
        <div v-else class="tiles fluid" ref="tiles">
          <pipeline-tile-grid
            :pipelines="pipelines"
            :group_by_groups="config.group_by_groups"
            :group_by_namespaces="config.group_by_namespaces"
            :show_history="config.beta_show_history"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Background from "./components/Background.vue";
import ConfigService from "@/lib/ConfigService";
import ContributeLink from "./components/ContributeLink.vue";
import ConfigurationForm from "./components/ConfigurationForm.vue";
import FavIcon from "./components/FavIcon.vue";
import FunSnow from "./components/FunSnow.vue";
import GitLab from "./clients/gitlab";
import GitlabService from "@/lib/GitlabService";
import LoadingSpinner from "./components/LoadingSpinner.vue";
import PipelineTileGrid from "./components/PipelineTileGrid.vue";
import is_christmas_snowtime from "./clients/fun_show";
import storage from "./lib/storage";
import { BButton } from "bootstrap-vue-next";
import { Config, GitLabProject, Pipeline } from "@/interfaces";
import { defineComponent } from "vue";

const defaultConfig: Config = {
  beta_order_by_failures_first: false,
  beta_show_only_protected_branches: false,
  beta_show_scheduled_builds: false,
  alpha_show_snow: true,
  beta_show_history: false,
  beta_no_polling: true,

  url: "https://gitlab.com",
  token: "",
  groups: "",
  projects: "",
  group_by_namespaces: false,
  group_by_groups: true,
  include_shared_projects: false,
};

// 🔧 URL Parameter für add_groups, add_projects und gitlab_url
function getConfigFromUrl(): {
  config: Partial<Config>;
  hasUrlConfig: boolean;
} {
  const urlParams = new URLSearchParams(window.location.search);
  const configGroup = urlParams.get("add_groups");
  const configProject = urlParams.get("add_projects");
  const gitlabUrl = urlParams.get("gitlab_url");

  const config: Partial<Config> = {};
  let hasUrlConfig = false;

  if (configGroup) {
    config.groups = configGroup;
    hasUrlConfig = true;
  }

  if (configProject) {
    config.projects = configProject;
    hasUrlConfig = true;
  }

  if (gitlabUrl) {
    config.url = gitlabUrl;
    hasUrlConfig = true;
  }

  return { config, hasUrlConfig };
}

const userConfigFromBrowser = storage.get("config") || {};
const urlConfigResult = getConfigFromUrl();

// 🔄 Additive Konfiguration: URL-Parameter zu bestehenden Werten hinzufügen
function mergeConfigAdditively(baseConfig: Config, urlConfig: Partial<Config>): Config {
  const result = { ...baseConfig };

  if (urlConfig.groups) {
    const existingGroups = result.groups ? result.groups.split(';').filter(g => g.trim()) : [];
    console.log("existing groups: " + existingGroups)
    const urlGroups = urlConfig.groups.split(';').filter(g => g.trim());
    console.log("adding groups: " + urlGroups)
    const combinedGroups = [...existingGroups, ...urlGroups];
    result.groups = [...new Set(combinedGroups)].sort().join(';'); // 📋 Sortiert + Duplikate entfernt
  }

  if (urlConfig.projects) {
    const existingProjects = result.projects ? result.projects.split(';').filter(p => p.trim()) : [];
    console.log("existing projects: " + existingProjects)
    const urlProjects = urlConfig.projects.split(';').filter(p => p.trim());
    console.log("adding projects: " + urlProjects)
    const combinedProjects = [...existingProjects, ...urlProjects];
    result.projects = [...new Set(combinedProjects)].sort().join(";"); // 📋 Sortiert + Duplikate entfernt
  }

  // 🌐 URL überschreiben (nicht additiv)
  if (urlConfig.url) {
    result.url = urlConfig.url;
  }

  return result;
}

export default defineComponent({
  name: "app",
  components: {
    Background,
    ConfigurationForm,
    ContributeLink,
    FavIcon,
    FunSnow,
    LoadingSpinner,
    PipelineTileGrid,
    // bootstrap-vue
    BButton,
  },
  data: () => {
    const baseConfig = {
      ...defaultConfig,
      ...userConfigFromBrowser,
    } as Config;

    // 🔄 URL-Parameter additiv hinzufügen
    const finalConfig = urlConfigResult.hasUrlConfig
      ? mergeConfigAdditively(baseConfig, urlConfigResult.config)
      : baseConfig;

    return {
      config: finalConfig,
      gitlabService: null as null | GitlabService,
      configService: null as null | ConfigService,
      projects: null as null | GitLabProject[],
      pipelines: [] as Pipeline[],
      pipelinePoll: 0 as number,
      // projectPoll: null as any,
      retryPoll: 0 as number,
      showConfiguration: urlConfigResult.hasUrlConfig, // 🎯 Auto-open bei URL-Config
      error: null as null | string | Error,
    };
  },
  computed: {
    configuring(): boolean {
      return (
        this.showConfiguration ||
        !this.config.token ||
        !this.config.url ||
        (!this.config.groups && !this.config.projects)
      );
    },
    loading(): boolean {
      return this.pipelines.length == 0;
    },
    show_snow_fun: is_christmas_snowtime,
  },
  watch: {
    async configuring(configuring: boolean) {
      if (!configuring) {
        await this.pollProjects();
      }
    },
    async projects(projects: undefined | GitLabProject[]) {
      if (projects) {
        await this.pollPipelines();
        this.stopPollingRetry();
      }
    },
  },
  methods: {
    retry() {
      this.error = null;
      this.projects = null;
      this.pollProjects();
    },
    async pollProjects() {
      this.stopPollingPipelines();
      await this.loadProjects(this.config);
    },
    async pollPipelines() {
      this.stopPollingPipelines();
      await this.loadPipelines();

      if (this.config.beta_no_polling) {
        return;
      }
      this.pipelinePoll = setInterval(() => {
        console.log("👻 another pipeline poll");
        this.loadPipelines();
      }, 5 * 1000);
    },
    autoRetry() {
      this.retryPoll = setInterval(() => this.retry(), 60 * 1000);
    },
    // stopPollingProjects() {
    //   if (this.projectPoll) {
    //     window.clearInterval(this.projectPoll)
    //   }
    // },
    stopPollingPipelines() {
      if (this.pipelinePoll) {
        window.clearInterval(this.pipelinePoll);
      }
    },
    stopPollingRetry() {
      if (this.retryPoll) {
        window.clearInterval(this.retryPoll);
      }
    },
    openConfiguration() {
      this.showConfiguration = true;
    },
    onSubmit(formConfig: Config) {
      this.stopPollingRetry();
      this.error = null;
      this.pipelines = [];
      this.projects = null;
      this.showConfiguration = false;
      this.config = formConfig;
      storage.set("config", this.config);
    },
    async loadProjects(config: Config) {
      if (!this.configService) {
        return;
      }
      try {
        this.projects = await this.configService.loadProjects(config);
        if (this.projects.length === 0) {
          this.setError("Could not find any projects.");
        }
      } catch (err) {
        this.handleError(err);
      }
    },
    async loadPipelines() {
      if (this.projects && this.gitlabService) {
        this.pipelines = await this.gitlabService.getProjectsPipelines(
          this.projects,
          this.config.beta_order_by_failures_first,
          this.config.beta_show_only_protected_branches,
          this.config.beta_show_scheduled_builds,
        );
      }
    },
    setError(err: string) {
      this.stopPollingPipelines();
      // this.stopPollingProjects()
      this.error = err;
    },
    handleError(err: any) {
      this.stopPollingPipelines();
      // this.stopPollingProjects()
      this.autoRetry();
      switch (true) {
        case err.code && err.code === "ECONNABORTED":
          return (this.error = `Could not establish connection to '${this.config.url}'.`);
        case err.response &&
          (err.response.status === 401 || err.response.status === 403):
          return (this.error = `Not authorized to get projects from '${this.config.url}'.`);
        case (err.response && err.response.status !== 200) ||
          err.response.status !== 203:
          return (this.error = `Could not get list of projects from '${this.config.url}'.`);
        default:
          // tslint:disable-next-line:no-console
          console.error(err);
          return (this.error = "Something unexpected went wrong.");
      }
    },
    resizeGrid() {
      const tiles = this.$refs.tiles as HTMLDivElement;
      if (!!tiles) {
        const grid = tiles.children[0] as HTMLDivElement;
        const tile = grid.children[0] as HTMLDivElement;
        if (tile) {
          const maxWidth =
            Math.floor(tiles.offsetWidth / tile.offsetWidth) * tile.offsetWidth;
          grid.style.maxWidth = maxWidth + "px";
        }
      }
    },
  },
  created() {
    if (!this.configuring) {
      const gitlab = new GitLab(this.config.url, this.config.token);
      this.gitlabService = new GitlabService(gitlab);
      this.configService = new ConfigService(gitlab);
      this.loadProjects(this.config);
    }
  },
  updated() {
    this.$nextTick(() => {
      this.resizeGrid();
    });
  },
  mounted() {
    window.addEventListener("resize", this.resizeGrid.bind(this));
  },
  unmounted() {
    window.removeEventListener("resize", this.resizeGrid.bind(this));
  },
});
</script>

<style lang="scss">
$red: #dc4644;
$green: #2f9e60;
$grey: #475158;

body {
  @import "stisla-theme/dist/scss/style.scss";

  &::after {
    display: none;
  }

  min-width: 22rem;
}

#app {
  padding: 2rem;
  position: relative;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  font-family: "Nunito", "Segoe UI", arial, serif;
  font-size: 1rem;
  color: #ffffff;
}

.configuration-form {
  margin-top: 2rem;
  max-width: 50rem;
  width: 100%;
}

.fluid {
  width: 100%;
}

button.options {
  z-index: 2;
  color: #ffffff;
  display: block;
  position: absolute;
  top: 1rem;
  right: 1rem;
  opacity: 1;

  &:hover {
    color: #ffffff;
  }

  svg {
    height: 1.4rem;
    width: 1.4rem;
  }
}

.error-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: $red;
  text-shadow: 2px 2px rgba(0, 0, 0, 0.5);

  .icon {
    width: 4rem;
    height: 4rem;
    margin-bottom: 1rem;
  }

  p,
  button {
    color: #ffffff;
  }
}

.tiles {
  display: flex;
  justify-content: center;
}
</style>
